<script lang="ts">
  /* Dashboard — the company's figures for a chosen period (2026-09-15).
     Overview: six small tiles, then one card per figure with its plan, how
     much of it is done and a 0–100% column. Dynamics: the same figures along
     a time line. Period plan: the owner's monthly plan. */
  import EmployeeDashboard from "./EmployeeDashboard.svelte";
  import DashboardDynamics from "./DashboardDynamics.svelte";
  import DashboardPlan from "./DashboardPlan.svelte";
  import { onMount, untrack } from "svelte";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import SectionGuide from "$components/SectionGuide.svelte";
  import SetupHint from "$components/SetupHint.svelte";
  import OnboardingChecklist from "$components/OnboardingChecklist.svelte";
  import LoadErrorState from "$components/LoadErrorState.svelte";
  import ModuleGate from "$components/ModuleGate.svelte";
  import { fetchOverview, openRecap, type Overview } from "$lib/billingRecap";
  import { runLoad, type LoadStatus } from "$lib/loadState";
  import { calendarIsoDate, companyCalendarDate, formatCompanyInstant, formatEur } from "$lib/format";
  import {
    BLOCKS,
    PERIODS,
    PERIOD_LABEL_KEYS,
    fetchDashboardStats,
    formatFigure,
    periodLabel,
    shiftAnchor,
    type DashboardStats,
    type Period,
  } from "$lib/dashboard";
  import { api } from "$lib/api";
  import { auth } from "$lib/auth";
  import { replace } from "svelte-spa-router";
  import { hasSurface, homePath } from "$lib/surfaces";
  import { hasFeature, membershipFor } from "$lib/permissions";
  import { checklist, expandChecklist, percentComplete } from "$lib/onboardingChecklist";
  import { subscribe, type RealtimeListener } from "$lib/realtime";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");
  const employeeView = $derived(membershipFor($auth,slug)?.permission_version===2 && !["owner","manager"].includes(membershipFor($auth,slug)?.role ?? ""));
  const tenantId = $derived(membershipFor($auth, slug)?.tenant.id);

  /* Free-trial countdown banner: constant (not dismissible) during the last
     7 days of the trial. Mirrors the first warning e-mail on the backend
     (apps/tenants/trials.py). Its button opens the recap figures — what the
     company used and what that would cost — for the owner; the «Тариф» page
     for anyone else. */
  const TRIAL_BANNER_DAYS = 7;
  const isOwner = $derived(membershipFor($auth, slug)?.role === "owner");
  const trialDaysLeft = $derived.by(() => {
    const ent = membershipFor($auth, slug)?.entitlements;
    if (!ent || ent.status !== "trialing" || !ent.trial_ends_at) return null;
    const ms = new Date(ent.trial_ends_at).getTime() - Date.now();
    if (ms <= 0) return null; // expired — the backend will strip Pro shortly
    const days = Math.ceil(ms / 86_400_000);
    return days <= TRIAL_BANNER_DAYS ? days : null;
  });
  const trialEndsOn = $derived.by(() => {
    const ent = membershipFor($auth, slug)?.entitlements;
    return ent?.trial_ends_at
      ? formatCompanyInstant(ent.trial_ends_at, slug)
      : "";
  });

  /* «Динамика» and «План периода» are the paid module «Dashboard:
     управление»; «Обзор» stays free. */
  const dashMgmt = $derived(hasFeature($auth, slug, "dashboard_mgmt"));

  /* Usage meter for a company on its own set: «Заказы: 78 из 100», or what
     the month's overage has come to. Read from the billing overview, which
     the owner and managers may read; anyone else simply has no meter. */
  let billing = $state<Overview | null>(null);
  const orderMeter = $derived.by(() => {
    if (!billing || billing.subscription.plan !== "custom") return null;
    const o = billing.usage?.quote?.orders;
    if (!o || !o.charged) return null;
    return o;
  });

  type Tab = "overview" | "dynamics" | "plan";
  const TABS: { id: Tab; labelKey: string }[] = [
    { id: "overview", labelKey: "dashboard.tab.overview" },
    { id: "dynamics", labelKey: "dashboard.tab.dynamics" },
    { id: "plan", labelKey: "dashboard.tab.plan" },
  ];
  let tab = $state<Tab>("overview");
  let period = $state<Period>("month");
  let anchor = $state(untrack(() => calendarIsoDate(companyCalendarDate(params?.slug))));
  let stats = $state<DashboardStats | null>(null);
  // Bumped on realtime events so the Dynamics tab refetches as well.
  let reloadKey = $state(0);

  type CreditAccount = { id: number; seller_tenant_id: number; buyer_tenant_id: number; currency: string; credit_limit: number; outstanding_balance: number; available: number; payment_terms: string };
  let creditAccounts = $state<CreditAccount[]>([]);

  // Failed loads render an error card with retry instead of silently showing
  // zeroed KPI placeholders (audit 2026-08-17, OE-A06).
  let loadStatus = $state<LoadStatus>("loading");
  let requestId = 0;

  function loadDashboardData() {
    if (!slug) return;
    runLoad((s) => (loadStatus = s), loadInner);
  }

  async function loadInner() {
    const id = ++requestId;
    const [statsRes, creditRes] = await Promise.all([
      fetchDashboardStats(slug, period, anchor),
      api.get(`/t/${slug}/credit/accounts`).catch(() => []),
    ]);
    if (id !== requestId) return;
    stats = statsRes;
    creditAccounts = (creditRes as CreditAccount[]) ?? [];
  }

  function choosePeriod(p: Period) {
    period = p;
    anchor = calendarIsoDate(companyCalendarDate(slug));
    loadDashboardData();
  }

  function step(direction: -1 | 1) {
    anchor = shiftAnchor(period, anchor, direction);
    loadDashboardData();
  }

  const atLatest = $derived(!stats || period === "total" || stats.end >= stats.today);

  function tone(pct: number | null | undefined): string {
    if (pct == null) return "none";
    if (pct >= 100) return "met";
    if (pct >= 50) return "mid";
    return "low";
  }

  /* Setup guide, always reachable from the top of the dashboard. The checklist
     used to be recoverable only from the left-nav identity block, so a stray
     click on its ✕ effectively hid onboarding for good. */
  let guideForced = $state(false);
  const checklistReady = $derived($checklist.slug === slug && $checklist.loaded);
  const setupPct = $derived(percentComplete($checklist));
  function openGuide() {
    guideForced = true;
    expandChecklist(slug);
    // The panel renders below the title; bring it into view when the page is
    // already scrolled down.
    requestAnimationFrame(() =>
      document
        .querySelector(".dashboard .onboard, .dashboard .onboard-summary")
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
    );
  }

  onMount(() => {
    // The short workspace has no dashboard; an old bookmark lands on orders.
    if (slug && !hasSurface($auth, slug, "nav.dashboard")) {
      replace(homePath($auth, slug));
      return;
    }
    if(employeeView)return;
    if (slug) loadDashboardData();
    if (slug) fetchOverview(slug).then((ov) => (billing = ov)).catch(() => {});
    let disposed = false;
    let realtime: RealtimeListener | null = null;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    if (tenantId) {
      subscribe(`tenant:${tenantId}:dashboard`, (event) => {
        if (!event.type.startsWith("order.") && event.type !== "document.ready") return;
        if (reloadTimer) clearTimeout(reloadTimer);
        reloadTimer = setTimeout(() => {
          loadDashboardData();
          reloadKey += 1;
        }, 100);
      }).then((listener) => {
        realtime = listener;
        if (disposed) listener.unsubscribe();
      });
    }
    return () => {
      disposed = true;
      if (reloadTimer) clearTimeout(reloadTimer);
      realtime?.unsubscribe();
    };
  });
</script>

<AppShell tenantSlug={slug} active="dashboard">
{#if employeeView}<EmployeeDashboard {slug}/>{:else}
  <div class="dashboard">
    {#if trialDaysLeft !== null}
      <div class="trial-banner" role="status">
        <span class="material-icons-outlined trial-icon" aria-hidden="true">schedule</span>
        <div class="trial-text">
          <strong>{$_("billing.dash.trialTitle", { values: { date: trialEndsOn } })}</strong>
          <span>{$_("billing.dash.trialBody", { values: { days: trialDaysLeft } })}</span>
        </div>
        {#if isOwner}
          <button type="button" class="trial-cta" onclick={() => openRecap(slug)}>{$_("billing.dash.trialRecap")}</button>
        {/if}
        <a class={isOwner ? "trial-link" : "trial-cta"} href={`#/t/${slug}/pricing`}>{$_("billing.dash.trialTariff")}</a>
      </div>
    {/if}
    <div class="page-title-block">
      <div class="title-row">
        <h1 class="page-title">{$_("dashboard.title")}</h1>
        <SectionGuide id="dashboard" />
      </div>
      {#if orderMeter}
        <a class="usage-meter" class:over={orderMeter.over > 0} href={`#/t/${slug}/pricing?tab=usage`}
           title={$_("billing.dash.usageHint")}>
          <span class="material-icons-outlined" aria-hidden="true">receipt_long</span>
          {#if orderMeter.included == null}
            {$_("billing.dash.ordersUnlimited", { values: { count: orderMeter.count } })}
          {:else if orderMeter.over > 0}
            {$_("billing.dash.ordersOver", { values: { over: orderMeter.over, amount: formatEur(orderMeter.amount) } })}
          {:else}
            {$_("billing.dash.orders", { values: { count: orderMeter.count, included: orderMeter.included } })}
          {/if}
        </a>
      {/if}
      {#if checklistReady}
        <button
          type="button"
          class="guide-btn"
          onclick={openGuide}
          title={$_("onboard.openGuide.hint")}
        >
          <span class="guide-label">
            <span class="material-icons-outlined" aria-hidden="true">help_outline</span>
            {$_("onboard.openGuide")}
          </span>
          <span class="setup-ring" style={`--pct: ${setupPct}`} title={`${setupPct}%`}>
            <span>{setupPct}%</span>
          </span>
        </button>
      {/if}
    </div>

    <OnboardingChecklist tenantSlug={slug} forceOpen={guideForced} />
    <SetupHint {slug} id="unsent_order" />

    <div class="dash-toolbar">
      <div class="seg" role="tablist">
        {#each TABS as t (t.id)}
          <button
            type="button"
            role="tab"
            class="seg-btn"
            class:on={tab === t.id}
            aria-selected={tab === t.id}
            onclick={() => (tab = t.id)}
          >{$_(t.labelKey)}</button>
        {/each}
      </div>

      {#if tab === "overview" || (tab === "dynamics" && dashMgmt)}
        <div class="period-tools">
          <div class="seg" role="group" aria-label={$_("dashboard.period.label")}>
            {#each PERIODS as p (p)}
              <button
                type="button"
                class="seg-btn"
                class:on={period === p}
                aria-pressed={period === p}
                onclick={() => choosePeriod(p)}
              >{$_(PERIOD_LABEL_KEYS[p])}</button>
            {/each}
          </div>
          {#if period !== "total"}
            <button type="button" class="icon-btn" onclick={() => step(-1)} title={$_("dashboard.period.prev")} aria-label={$_("dashboard.period.prev")}>
              <span class="material-icons-outlined" aria-hidden="true">chevron_left</span>
            </button>
          {/if}
          <span class="period-label">{stats ? periodLabel(period, stats.start, stats.end, $_) : ""}</span>
          {#if period !== "total"}
            <button type="button" class="icon-btn" onclick={() => step(1)} disabled={atLatest} title={$_("dashboard.period.next")} aria-label={$_("dashboard.period.next")}>
              <span class="material-icons-outlined" aria-hidden="true">chevron_right</span>
            </button>
          {/if}
        </div>
      {/if}
    </div>

    {#if tab === "plan"}
      <ModuleGate tenantSlug={slug} feature="dashboard_mgmt">
        <DashboardPlan {slug} onchanged={loadDashboardData} />
      </ModuleGate>
    {:else if tab === "dynamics"}
      <ModuleGate tenantSlug={slug} feature="dashboard_mgmt">
        <DashboardDynamics {slug} {period} {anchor} {reloadKey} />
      </ModuleGate>
    {:else if loadStatus === "error"}
      <LoadErrorState onretry={loadDashboardData} />
    {:else}
    <div class="kpi-grid">
      {#each BLOCKS as block (block.id)}
        <div class="kpi-card card">
          <div class="kpi-top">
            <span class="material-icons-outlined kpi-icon" aria-hidden="true">{block.icon}</span>
            <span class="kpi-label">{$_(block.labelKey)}</span>
          </div>
          <div class="kpi-values">
            {#each block.parts as part, i (part.metric)}
              {#if i > 0}<span class="kpi-sep">/</span>{/if}
              <span class="kpi-part">
                <span class="kpi-value">{stats ? formatFigure(part.metric, stats.metrics[part.metric].actual, true) : "-"}</span>
                <span class="kpi-sub">{$_(part.partKey)}</span>
              </span>
            {/each}
          </div>
        </div>
      {/each}
    </div>

    {#if creditAccounts.length > 0}
      <div class="credit-strip">
        {#each creditAccounts as acct}
          <div class="credit-card card">
            <div class="credit-label">{$_("dashboard.credit.available")}</div>
            <div class="credit-value">{acct.currency} {formatEur(acct.available)}</div>
            <div class="credit-sub">
              {$_("dashboard.credit.limit", { values: { limit: `${acct.currency} ${formatEur(acct.credit_limit)}` } })}
              · {$_("dashboard.credit.terms", { values: { terms: acct.payment_terms } })}
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <div class="goal-grid">
      {#each BLOCKS as block (block.id)}
        <div class="card goal-card">
          <h3 class="goal-title">
            <span class="material-icons-outlined" aria-hidden="true">{block.icon}</span>
            {$_(block.labelKey)}
          </h3>
          <div class="goal-body">
            {#each block.parts as part (part.metric)}
              {@const f = stats?.metrics[part.metric]}
              <div class="goal-part">
                <div class="goal-figures">
                  <div class="goal-caption">{$_(part.partKey)}</div>
                  <div class="goal-actual">{f ? formatFigure(part.metric, f.actual) : "-"}</div>
                  <div class="goal-line">
                    {$_("dashboard.goal.target")}: <strong>{f?.goal != null ? formatFigure(part.metric, f.goal) : "-"}</strong>
                  </div>
                  <div class="goal-line">
                    {$_("dashboard.goal.done")}: <strong class="tone-text--{tone(f?.pct)}">{f?.pct != null ? `${Math.round(f.pct)}%` : "-"}</strong>
                  </div>
                </div>
                <div class="goal-bar" title={f?.pct != null ? `${Math.round(f.pct)}%` : ""}>
                  <span class="goal-bar-mark">100%</span>
                  <div class="goal-bar-track">
                    <div class="goal-bar-fill tone--{tone(f?.pct)}" style="height:{Math.min(100, Math.max(0, f?.pct ?? 0))}%"></div>
                  </div>
                  <span class="goal-bar-mark">0%</span>
                </div>
              </div>
            {/each}
          </div>
          {#if stats && block.parts.every((p) => stats?.metrics[p.metric].goal == null)}
            <div class="goal-empty">
              {$_("dashboard.goal.none")}
              {#if stats.can_edit_plan}
                · <button type="button" class="link-btn" onclick={() => (tab = "plan")}>{$_("dashboard.goal.set")}</button>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
    {/if}
  </div>
{/if}
</AppShell>

<style>
  .dashboard { display: flex; flex-direction: column; gap: 24px; }

  .trial-banner {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 20px; border-radius: var(--radius);
    background: #fff8e1; border: 1px solid #ffe082;
  }
  .trial-icon { color: #e65100; font-size: 22px; }
  .trial-text { display: flex; flex-direction: column; gap: 2px; flex: 1; font-size: 13px; }
  .trial-text strong { font-size: 14px; color: var(--color-surface-darker); }
  .trial-text span { color: var(--color-text-muted); }
  .trial-cta {
    padding: 8px 16px; border-radius: var(--radius); white-space: nowrap;
    background: var(--color-primary); color: #fff; font-size: 13px;
    font-weight: 600; text-decoration: none;
  }
  .trial-cta:hover { filter: brightness(1.05); }
  button.trial-cta { border: 0; font-family: inherit; cursor: pointer; }
  .trial-link { font-size: 13px; font-weight: 600; color: var(--color-primary); white-space: nowrap; }
  @media (max-width: 640px) { .trial-banner { flex-wrap: wrap; } }

  .usage-meter {
    display: inline-flex; align-items: center; gap: 6px; margin-left: auto;
    padding: 6px 12px; border-radius: var(--radius); border: 1px solid var(--color-divider);
    background: #fff; font-size: 13px; font-weight: 600; color: var(--color-surface-darker);
    text-decoration: none; white-space: nowrap; font-variant-numeric: tabular-nums;
  }
  .usage-meter:hover { border-color: var(--color-primary); text-decoration: none; }
  .usage-meter .material-icons-outlined { font-size: 18px; color: var(--color-primary); }
  .usage-meter.over { border-color: #f3d6a6; background: var(--color-warning-soft); }

  /* global.css makes .page-title-block a column; the Setup guide button needs
     to sit opposite the title, so this one is a row. */
  .page-title-block {
    display: flex; flex-direction: row; align-items: center;
    justify-content: space-between; gap: 16px; flex-wrap: wrap;
  }
  .title-row { display: flex; align-items: center; gap: 8px; }
  .guide-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 5px 4px 14px; border-radius: var(--radius);
    border: 1px solid var(--color-divider); background: #fff;
    font: inherit; font-size: 13px; font-weight: 600;
    color: var(--color-primary); cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
  }
  .guide-btn:hover { border-color: var(--color-primary); background: rgba(33,133,213,0.06); }
  .guide-label { display: inline-flex; align-items: center; gap: 6px; }
  .guide-label .material-icons-outlined { font-size: 18px; }
  .setup-ring {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: conic-gradient(
      var(--color-primary) calc(var(--pct) * 1%),
      var(--color-divider) 0
    );
  }
  .setup-ring::before {
    content: "";
    grid-area: 1 / 1;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #fff;
  }
  .setup-ring span {
    grid-area: 1 / 1;
    color: var(--color-primary);
    font-size: 10px;
    font-weight: 700;
  }

  .page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--color-surface-darker);
  }

  .dash-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px 16px; flex-wrap: wrap;
  }
  .seg {
    display: inline-flex; flex-wrap: wrap; overflow: hidden;
    border: 1px solid var(--color-divider); border-radius: 7px; background: #fff;
  }
  .seg-btn {
    padding: 7px 14px; border: 0; background: #fff; font: inherit; font-size: 13px;
    color: var(--color-text-muted); cursor: pointer;
  }
  .seg-btn + .seg-btn { border-left: 1px solid var(--color-divider); }
  .seg-btn.on { background: var(--color-primary); color: #fff; font-weight: 600; }
  .period-tools { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .period-label { min-width: 120px; text-align: center; font-size: 13px; font-weight: 600; color: var(--color-surface-darker); }
  .icon-btn {
    display: inline-grid; place-items: center; width: 32px; height: 32px; padding: 0;
    border: 1px solid var(--color-divider); border-radius: 7px; background: #fff;
    color: var(--color-surface-darker); cursor: pointer;
  }
  .icon-btn:disabled { opacity: 0.4; cursor: default; }
  .icon-btn .material-icons-outlined { font-size: 20px; }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 16px;
  }
  @media (max-width: 1400px) { .kpi-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  @media (max-width: 800px) { .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 500px) { .kpi-grid { grid-template-columns: minmax(0, 1fr); } }

  .kpi-card {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .kpi-top { display: flex; align-items: center; gap: 8px; }
  .kpi-icon {
    font-size: 20px;
    color: var(--color-primary);
    opacity: 0.85;
  }
  .kpi-label {
    font-size: 13px;
    color: var(--color-text-muted);
  }
  .kpi-values { display: flex; align-items: flex-end; gap: 8px; flex-wrap: wrap; }
  .kpi-part { display: flex; flex-direction: column; gap: 3px; }
  .kpi-value {
    font-size: 26px;
    font-weight: 700;
    color: var(--color-surface-darker);
    line-height: 1;
  }
  .kpi-sub { font-size: 11px; color: var(--color-text-muted); }
  .kpi-sep { font-size: 22px; color: var(--color-divider); line-height: 1; padding-bottom: 16px; }

  .credit-strip { display: flex; gap: 16px; flex-wrap: wrap; }
  .credit-card { padding: 16px 20px; flex: 1; min-width: 220px; }
  .credit-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    color: var(--color-text-muted); margin-bottom: 4px; }
  .credit-value { font-size: 24px; font-weight: 700; color: var(--color-primary); }
  .credit-sub { font-size: 12px; color: var(--color-text-muted); margin-top: 4px; }

  .goal-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
  @media (max-width: 1200px) { .goal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 700px) { .goal-grid { grid-template-columns: minmax(0, 1fr); } }

  .goal-card { padding: 18px 22px; display: flex; flex-direction: column; gap: 12px; }
  .goal-title {
    display: flex; align-items: center; gap: 8px; margin: 0;
    font-size: 15px; font-weight: 600; color: var(--color-surface-darker);
  }
  .goal-title .material-icons-outlined { font-size: 20px; color: var(--color-primary); }
  .goal-body { display: flex; gap: 20px; flex-wrap: wrap; }
  .goal-part { flex: 1; min-width: 150px; display: flex; justify-content: space-between; gap: 12px; }
  .goal-figures { display: flex; flex-direction: column; gap: 4px; }
  .goal-caption { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted); }
  .goal-actual { font-size: 30px; font-weight: 700; line-height: 1.1; color: var(--color-surface-darker); }
  .goal-line { font-size: 12.5px; color: var(--color-text-muted); }
  .goal-line strong { color: var(--color-surface-darker); }

  .goal-bar { display: flex; flex-direction: column; align-items: center; gap: 4px; }
  .goal-bar-mark { font-size: 10px; color: var(--color-text-muted); }
  .goal-bar-track {
    position: relative; width: 30px; height: 120px; border-radius: 6px;
    background: var(--color-divider); overflow: hidden;
  }
  .goal-bar-fill { position: absolute; left: 0; right: 0; bottom: 0; border-radius: 6px 6px 0 0; transition: height 0.3s; }
  .tone--low { background: #e65100; }
  .tone--mid { background: var(--color-primary); }
  .tone--met { background: #2e7d32; }
  .tone--none { background: transparent; }
  .tone-text--low { color: #e65100 !important; }
  .tone-text--mid { color: var(--color-primary) !important; }
  .tone-text--met { color: #2e7d32 !important; }

  .goal-empty { font-size: 12.5px; color: var(--color-text-muted); }
  .link-btn { padding: 0; border: 0; background: none; font: inherit; color: var(--color-primary); cursor: pointer; }
  .link-btn:hover { text-decoration: underline; }
</style>
